import { FC, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generatePath } from 'react-router';
import { useExternalUserSubjectsSlice } from '@restore/ExternalUserSubjects';
import { Site } from '@restore/ExternalUserSubjects/ExternalUserSubjects.types';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import { useDefaultVisitSelection } from '@hooks/useDefaultVisitSelection';
import { getVisitKey } from '@utils/getVisitKey';
import { VisitTabTooltip } from '@components/VisitTabTooltip';
import { Tabs } from '@dynamika-shared/ui/Tabs/Tabs';
import { routesPath } from '@router/routes';
import { SubjectDetailHeader } from './components/SubjectDetailHeader';
import { VisitFileList } from './components/VisitFileList';
import { useVisitFiles } from './hooks/useVisitFiles';

export const SubjectDetail: FC = () => {
  const { patientId, clinicalTrialId } = useParams<{
    patientId: string;
    clinicalTrialId?: string;
  }>();
  const navigate = useNavigate();

  const [state, actions, context] = useExternalUserSubjectsSlice((s, a) => [
    s,
    a,
    s?.context,
  ]);

  const [selectedVisit, setSelectedVisit] = useState<string>('');

  const subjectDetails = state?.subjectDetails;
  const loading = state?.loading;
  const sites = context?.sites || [];
  const visits = useMemo(
    () => subjectDetails?.visits || [],
    [subjectDetails?.visits]
  );

  const activeVisitKey = selectedVisit || getVisitKey(visits[0]);
  const currentVisit = visits.find(v => getVisitKey(v) === activeVisitKey);
  const currentVisitKey = getVisitKey(currentVisit);
  const visitName = currentVisit?.visitName || '...';

  const {
    files,
    visitIdsWithFiles,
    uploadsLoading,
    handleDownloadFile,
    downloadingFileIds,
  } = useVisitFiles({
    clinicalTrialId,
    patientId,
    patientCode: subjectDetails?.patientCode,
    visitName,
    selectedVisit,
  });

  useBreadcrumbs(
    [
      ['Subject List', 'externalUserSubjectsClinicalTrial'],
      [
        subjectDetails?.patientCode ?? '...',
        'externalUserSubjectDetailClinicalTrial',
      ],
      [visitName, 'externalUserSubjectDetailClinicalTrial'],
    ],
    [clinicalTrialId, patientId]
  );

  useEffect(() => {
    // Reset any subject left over from a previously viewed CT/subject before
    // loading the new one, so stale visit tabs never render (D7-7158).
    setSelectedVisit('');
    actions?.clearSubjectDetails?.();

    if (
      patientId &&
      clinicalTrialId &&
      actions?.fetchExternalUserSubjectDetails
    ) {
      actions.fetchExternalUserSubjectDetails({ patientId, clinicalTrialId });
    }

    // Clear on unmount so leaving the page never leaks visits into the next subject.
    return () => {
      actions?.clearSubjectDetails?.();
    };
    // actions is memoized on [dispatch] in createSliceHook — omitted per
    // slice-hook convention; depend only on the route params.
  }, [patientId, clinicalTrialId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (clinicalTrialId && actions?.fetchContext) {
      actions.fetchContext({ clinicalTrialId });
    }
  }, [clinicalTrialId, actions]);

  // Visits without uploaded files are disabled, so default to one that has files.
  useDefaultVisitSelection(
    visits,
    selectedVisit,
    setSelectedVisit,
    visitIdsWithFiles
  );

  const currentSite = sites.find((s: Site) => s.id === subjectDetails?.siteId);
  const siteLabel = currentSite?.code || '-';

  const tabsItems = visits.map(visit => {
    const visitKey = getVisitKey(visit);
    const hasFiles = visitIdsWithFiles.has(visitKey);
    return {
      key: visitKey,
      // Visits without uploaded files are disabled.
      disabled: !hasFiles,
      label: (
        <VisitTabTooltip
          visitName={visit.visitName}
          hasData={hasFiles}
          isActive={visitKey === selectedVisit}
        >
          <span className='text-1624 font-medium'>{visit.visitName}</span>
        </VisitTabTooltip>
      ),
      children: null,
    };
  });

  const handleTabChange = (visitId: string) => {
    setSelectedVisit(visitId);
  };

  const goBack = () => {
    if (!clinicalTrialId) return;
    const url = generatePath(routesPath.externalUserSubjectsClinicalTrial, {
      clinicalTrialId,
    });
    navigate(url);
  };

  return (
    <div className='flex h-full flex-col'>
      <SubjectDetailHeader
        patientCode={subjectDetails?.patientCode || patientId!}
        dob={subjectDetails?.dob}
        siteLabel={siteLabel}
        cohortId={subjectDetails?.cohortId}
        onBack={goBack}
      />

      {tabsItems.length > 0 && (
        <div className='mb-4'>
          <Tabs
            activeKey={selectedVisit}
            items={tabsItems}
            onChange={handleTabChange}
            blocked={false}
          />
        </div>
      )}

      <div className='flex-1'>
        {loading && (
          <div className='flex h-full items-center justify-center'>
            <div className='text-spanishGray'>Loading subject details...</div>
          </div>
        )}

        {!loading && selectedVisit && currentVisit && (
          <VisitFileList
            visitName={currentVisit.visitName}
            visitId={currentVisitKey}
            siteId={subjectDetails?.siteId || ''}
            files={files}
            loading={uploadsLoading}
            onDownload={handleDownloadFile}
            downloadingFileIds={downloadingFileIds}
            // visitIdsWithFiles is keyed by file.visitId (the visit instance id);
            // planned visits (no id) fall back to visitDefinitionId via getVisitKey
            // and are never present here, so hasFiles is correctly false for them.
            hasFiles={visitIdsWithFiles.has(currentVisitKey)}
          />
        )}
      </div>
    </div>
  );
};
